/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { Package, FileText, Users, Gift, ShieldAlert, Wallet, CreditCard, Coins, ShieldCheck, ShoppingBag, MessageSquare, Code, BarChart3, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Features: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const features = [
    {
        icon: Package,
        title: "Inventory Sync",
        desc: "Real-time, blockchain-verified stock updates prevent overselling and ensure accurate inventory across all channels."
    },
    {
        icon: FileText,
        title: "Tax Automation",
        desc: "Automated sales tax calculation with exportable, audit-ready reports for seamless compliance."
    },
    {
        icon: Users,
        title: "Role Management",
        desc: "Granular role-based permissions for managers, cashiers, auditors, and custom roles to maintain secure operational control."
    },
    {
        icon: Gift,
        title: "Loyalty & Rewards",
        desc: "Tokenized loyalty programs leveraging NFTs and GHOST tokens to incentivize repeat customers and enhance engagement."
    },
    {
        icon: ShieldAlert,
        title: "Fraud Protection",
        desc: "AI-powered anomaly detection automatically flags and blocks suspicious wallets, patterns, and transactions."
    },
    {
        icon: Wallet,
        title: "Multi-Chain Wallet",
        desc: "Connect, monitor, and manage wallets across Sui, Solana, Ethereum, and other supported blockchains from a single interface."
    },
    {
        icon: CreditCard,
        title: "Payment Integrations",
        desc: "Seamless support for Stripe, MoonPay, Coinbase Pay, and Solana Pay, enabling fast and secure fiat-to-crypto transactions."
    },
    {
        icon: Coins,
        title: "Token Suite",
        desc: "Track, wrap, unwrap, and stake GHOST, wGHOST, and veGHOST tokens in real time for full transparency."
    },
    {
        icon: ShieldCheck,
        title: "Escrow & Settlement",
        desc: "On-chain escrow contracts with milestone tracking and automated fund release ensure trustless and transparent settlements."
    },
    {
        icon: ShoppingBag,
        title: "Marketplace",
        desc: "A multi-chain marketplace where merchants and users can securely buy, sell, and trade digital assets and tokenized goods."
    },
    {
        icon: MessageSquare,
        title: "Chat & AI Agents",
        desc: "Integrated AI support: Real-time chat, automated Assistants for guidance, and specialized Agents for complex tasks."
    },
    {
        icon: Code,
        title: "Developer APIs",
        desc: "Comprehensive SDKs and REST APIs for deep integrations, workflow automation, and custom reporting."
    },
    {
        icon: BarChart3,
        title: "Analytics & Reporting",
        desc: "Advanced dashboards provide insights into transactions, sales, and fraud detection, with exportable reports for auditing."
    }
  ];

  const totalPages = Math.ceil(features.length / itemsPerPage);
  const currentFeatures = features.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const nextPage = () => {
      if (currentPage < totalPages) setCurrentPage(curr => curr + 1);
  };

  const prevPage = () => {
      if (currentPage > 1) setCurrentPage(curr => curr - 1);
  };

  return (
    <section id="features" className="bg-[#F8F9FA] dark:bg-[#0A0B0F] py-32 transition-colors duration-300">
      <div className="container mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-20">
           <h2 className="font-serif text-4xl md:text-5xl text-[#0A0B0F] dark:text-white mb-6">Features & <span className="italic text-gray-500 dark:text-gray-400">Integrations</span></h2>
           <p className="text-gray-500 dark:text-gray-400 text-lg">A production-ready suite for the sovereign economy.</p>
        </div>

        <div className="min-h-[600px] flex flex-col justify-between">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <AnimatePresence mode="wait">
                    {currentFeatures.map((f, i) => (
                        <motion.div 
                            key={f.title}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3, delay: i * 0.05 }}
                            className="bg-white dark:bg-[#15171C] p-8 rounded-2xl border border-gray-100 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20 hover:shadow-xl transition-all duration-300 group"
                        >
                        <div className="w-12 h-12 bg-[#0A0B0F] dark:bg-white rounded-lg flex items-center justify-center mb-6 text-white dark:text-black group-hover:scale-110 transition-transform duration-300">
                            <f.icon size={24} strokeWidth={1.5} />
                        </div>
                        <h3 className="font-serif text-xl font-bold text-[#0A0B0F] dark:text-white mb-4">{f.title}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{f.desc}</p>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>

            {/* Pagination Controls */}
            <div className="flex justify-center items-center gap-6 mt-16">
                <button 
                    onClick={prevPage} 
                    disabled={currentPage === 1}
                    className="p-3 rounded-full border border-stone-200 dark:border-white/10 text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                    <ChevronLeft size={24} />
                </button>
                
                <div className="flex gap-2">
                    {Array.from({ length: totalPages }).map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setCurrentPage(i + 1)}
                            className={`w-3 h-3 rounded-full transition-all ${currentPage === i + 1 ? 'bg-nobel-gold scale-125' : 'bg-stone-300 dark:bg-stone-700 hover:bg-stone-400 dark:hover:bg-stone-600'}`}
                        />
                    ))}
                </div>

                <button 
                    onClick={nextPage} 
                    disabled={currentPage === totalPages}
                    className="p-3 rounded-full border border-stone-200 dark:border-white/10 text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                    <ChevronRight size={24} />
                </button>
            </div>
        </div>
      </div>
    </section>
  );
};
